import { useEffect, useMemo } from 'react';
import { cn } from '../../lib/utils';
import type { ThreadMessage } from '../../types/email';
import { Skeleton } from '../../ui/skeleton';
import { UserAvatar } from '../../ui/user-avatar';
import { MessageBody } from './MessageBody';
import { RetainedMessageBody } from './RetainedMessageBody';

export type { ThreadMessage };

export interface ThreadMessageListProps {
  messages: ThreadMessage[] | undefined;
  isLoading: boolean;
  /**
   * The provider message id an Ask or an AI citation currently points at.
   *
   * Highlighted and scrolled to. The *provider* id rather than the internal one because
   * this list is fed by the provider fetch and has no internal ids to match against;
   * callers translate before passing it in.
   */
  highlightedMessageId?: string | null;
  /**
   * Where the timeline came from. `retained` means the provider could not return the
   * thread and this is the workspace's own copy — which is a fact about the mailbox the
   * reader needs, not a failure to apologise for.
   */
  source?: 'provider' | 'retained';
}

/** The DOM id a message row carries, so a caller can scroll to it without a ref map. */
export function threadMessageAnchorId(providerMessageId: string): string {
  return `clinic-message-${providerMessageId}`;
}

export function ThreadMessageList({
  messages,
  isLoading,
  highlightedMessageId,
  source = 'provider',
}: ThreadMessageListProps) {
  const messagesWithSubjectFlag = useMemo(() => {
    const result: Array<{ message: ThreadMessage; showSubject: boolean }> = [];
    let previousSubject = '';
    for (const message of messages || []) {
      const showSubject = !!(message.subject && message.subject !== previousSubject);
      result.push({ message, showSubject });
      previousSubject = message.subject || previousSubject;
    }
    return result;
  }, [messages]);

  // Scrolls when the selection changes, not on every render. `block: 'center'` rather
  // than the default `start`, so the message lands where the eye already is instead of
  // jumping to the top of the viewport.
  useEffect(() => {
    if (!highlightedMessageId) return;
    const element = document.getElementById(threadMessageAnchorId(highlightedMessageId));
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightedMessageId]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[0, 1].map((i) => (
          <div key={i} className="border rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-3">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="space-y-1.5 flex-1">
                <Skeleton className="h-3 w-1/3" />
                <Skeleton className="h-3 w-1/5" />
              </div>
            </div>
            <Skeleton className="h-16 w-full" />
          </div>
        ))}
      </div>
    );
  }

  if (!messages || messages.length === 0) {
    return (
      <div className="rounded-xl border border-dashed p-6 text-center">
        <p className="text-xs text-muted-foreground italic">No messages found in this thread.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {source === 'retained' && (
        <p className="max-w-measure text-sm leading-relaxed text-murmur">
          This conversation is no longer available from the mailbox, so it is shown from the copy
          this workspace kept.
        </p>
      )}
      {messagesWithSubjectFlag.map(({ message, showSubject }) => {
        const senderLabel = message.from.name || message.from.email;
        const timestamp = message.timestampMs ? new Date(message.timestampMs) : null;

        const isHighlighted = highlightedMessageId === message.id;

        return (
          <div
            key={message.id}
            id={threadMessageAnchorId(message.id)}
            // A hairline in honey, not a fill: this marks the system pointing at
            // something, which is what that colour is for, and a coloured background
            // would read as an alert.
            className={cn(
              'border rounded-xl bg-card/30 p-4 space-y-3 transition-colors',
              isHighlighted && 'border-honey'
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <UserAvatar
                  user={{ fullName: message.from.name, email: message.from.email }}
                  name={senderLabel}
                  size="md"
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{senderLabel}</p>
                  <p className="text-xs text-muted-foreground truncate">{message.from.email}</p>
                </div>
              </div>
              {timestamp && (
                <span className="text-xs text-muted-foreground shrink-0">
                  {timestamp.toLocaleString()}
                </span>
              )}
            </div>
            {showSubject && (
              <p className="font-display text-xs font-normal text-muted-foreground">
                {message.subject}
              </p>
            )}
            {message.retained && message.clinicMessageId ? (
              <RetainedMessageBody
                clinicMessageId={message.clinicMessageId}
                snippet={message.bodyText}
                hasStoredContent={message.hasStoredContent ?? false}
              />
            ) : (
              <MessageBody html={message.bodyHtml} text={message.bodyText} />
            )}
          </div>
        );
      })}
    </div>
  );
}
